/**
 * Hook to obtain a gqlFetch function for invoking GraphQL operations.
 *
 * For details on usage, see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/1927577736/useGql.
 */
import * as React from "react";

import * as wbd from "@khanacademy/wonder-blocks-data";
import type {GqlOperation} from "@khan/data-gql-core";

import {DefaultFastlyQueryScope} from "./constants.ts";
import {toNormalizedOperation} from "./to-normalized-operation.ts";
import type {GqlFetchFn, GqlFetchOptions} from "./types.ts";
import {useRequestIdBuilder} from "./use-request-id-builder.ts";

/**
 * Hook to obtain a gqlFetch function for performing GraphQL requests.
 *
 * The fetch function will resolve null if the request was aborted, otherwise
 * it will resolve the data returned by the GraphQL server.
 *
 * It will reject with an error if the request failed or the response was
 * invalid.
 *
 * Finally, note that this only uses local caching for fastly-cacheable queries.
 * All other calls will lead to a network request.
 */
export const useGql = (): GqlFetchFn => {
    // We get the Wonder Blocks Data function.
    // This already manages default context for us.
    const wbdFetch = wbd.useGql();

    // We need to be able to get a request ID for Fastly-cacheable queries.
    const requestIdBuilder = useRequestIdBuilder();

    // Now we want a wrapper around that, which we only need to update
    // if the Wonder Blocks Data function changes.
    // The webapp version needs to map webapp operations and context
    // to the Wonder Blocks compatible equivalent.
    const gqlFetch = React.useCallback(
        async <
            TData extends Record<any, any>,
            TVariables extends Record<any, any>,
        >(
            operation: GqlOperation<TData, TVariables>,
            {variables, context}: GqlFetchOptions<TVariables> = Object.freeze({
                variables: undefined,
                context: undefined,
            }),
        ): Promise<TData> => {
            const wbdOperation = toNormalizedOperation(operation);

            const isFastlyCacheable =
                wbdOperation.type === "query" && context?.fastlyCaching != null;
            const requestId = requestIdBuilder(
                operation,
                variables,
                context ?? {},
            );
            const cachedResult = isFastlyCacheable
                ? wbd.SharedCache.get(DefaultFastlyQueryScope, requestId)
                : null;
            if (cachedResult != null) {
                // We assert that the cached value is the same type as what we
                // want to return.
                return cachedResult as TData;
            }

            const {curriculum, module, targetLocale, fastlyCaching} =
                context ?? {};
            const result = await wbdFetch<TData, TVariables>(wbdOperation, {
                variables,
                context: {
                    module,
                    targetLocale,
                    curriculum,
                    fastlyCaching: fastlyCaching
                        ? (fastlyCaching as string)
                        : undefined,
                },
            });

            if (isFastlyCacheable) {
                // We need to update the cache.
                wbd.SharedCache.set(DefaultFastlyQueryScope, requestId, result);
            }

            return result;
        },
        [wbdFetch, requestIdBuilder],
    );

    return gqlFetch;
};
